import {pluralize} from '~/common/utils';
import {
  DeliveryStatus,
  type Guest,
  isHostGuest,
  type RsvpResponse,
} from '~/ViewInvitation/types/guests';
import {SortBy, type SortDirection} from '~/ViewInvitation/types/reducer';

export const filterBy = (guests: Guest[], filter?: RsvpResponse) => {
  if (!filter) return guests;
  return guests.filter((guest) => guest.rsvpResponse === filter);
};

export const queryBy = (guests: Guest[], query?: string) => {
  if (!query) return guests;
  const lowerCaseQuery = query.toLowerCase();
  return guests.filter((guest) => {
    if (isHostGuest(guest)) {
      return (
        guest.name.toLowerCase().includes(lowerCaseQuery) ||
        guest.email?.toLowerCase()?.includes(lowerCaseQuery) ||
        guest.phone?.toLowerCase()?.includes(lowerCaseQuery)
      );
    }
    return guest.name.toLowerCase().includes(lowerCaseQuery);
  });
};

const sortFailedFirst = (guestA: Guest, guestB: Guest) => {
  if (isHostGuest(guestA) && isHostGuest(guestB)) {
    if (
      guestA.deliveryStatus === DeliveryStatus.FAILED &&
      guestB.deliveryStatus !== DeliveryStatus.FAILED
    ) {
      return -1;
    }
    if (
      guestA.deliveryStatus !== DeliveryStatus.FAILED &&
      guestB.deliveryStatus === DeliveryStatus.FAILED
    ) {
      return 1;
    }
  }
  return 0;
};

const sortByField = (guestA: Guest, guestB: Guest, field: keyof Guest) => {
  const fieldA = guestA[field];
  const fieldB = guestB[field];
  const failedSort = sortFailedFirst(guestA, guestB);
  if (failedSort !== 0) return failedSort;
  if (!!fieldA && !fieldB) return -1;
  if (!fieldA && !!fieldB) return 1;
  if (!!fieldA && !!fieldB) {
    if (!!guestA.updated && !guestB.updated) return -1;
    if (!guestA.updated && !!guestB.updated) return 1;
    if (!!guestA.updated && !!guestB.updated) {
      return guestA.updated.localeCompare(guestB.updated);
    }
  }
  return 0;
};

const sortByComments = (guestA: Guest, guestB: Guest) => {
  const failedSort = sortFailedFirst(guestA, guestB);
  if (failedSort !== 0) return failedSort;
  if (guestA.comment && !guestB.comment) {
    return -1;
  }
  if (!guestA.comment && guestB.comment) {
    return 1;
  }

  // Secondary sorting criterion: sort by name alphabetically (ascending)
  return guestA.name.localeCompare(guestB.name);
};

const deliveryStatusSortOrder = [
  DeliveryStatus.FAILED,
  DeliveryStatus.UNKNOWN,
  DeliveryStatus.SENDING,
  DeliveryStatus.DELIVERED,
] as const;
const sortByDeliveryStatus = (guestA: Guest, guestB: Guest) => {
  if (isHostGuest(guestA) && isHostGuest(guestB)) {
    const indexA = deliveryStatusSortOrder.indexOf(guestA.deliveryStatus);
    const indexB = deliveryStatusSortOrder.indexOf(guestB.deliveryStatus);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    if (indexA === -1 && indexB === -1) {
      return 0;
    }
    if (indexA === -1) {
      return 1;
    }
    return -1;
  }
  return 0;
};

export const sortBy = (guests: Guest[], sort: SortBy, direction: SortDirection) => {
  return guests?.toSorted((a, b) => {
    if (sort === SortBy.ALPHA) {
      const failedSort = sortFailedFirst(a, b);
      if (failedSort !== 0) return failedSort;
      if (direction === 'asc') {
        return a.name.localeCompare(b.name);
      }
      return b.name.localeCompare(a.name);
    }
    if (sort === SortBy.RSVP) {
      if (direction === 'asc') {
        return sortByField(a, b, 'rsvpResponse');
      }
      return sortByField(b, a, 'rsvpResponse');
    }
    if (sort === SortBy.COMMENTS) {
      // we ignore sort direction, since product doesn't want the user to have control over sort direction
      return sortByComments(a, b);
    }
    // sort === SortBy.DELIVERY_STATUS
    if (direction === 'asc') {
      return sortByDeliveryStatus(a, b);
    }
    return sortByDeliveryStatus(b, a);
  });
};

export const generateHeadcountString = (guest: Guest, familyHeadcountEnabled: boolean) => {
  const texts: string[] = [];
  const label = familyHeadcountEnabled ? 'Adult' : 'Guest';
  if (guest.numberOfAdults > 0) texts.push(pluralize(label, guest.numberOfAdults));
  if (familyHeadcountEnabled && guest.numberOfKids > 0)
    texts.push(pluralize('Kid', guest.numberOfKids));
  return texts.join(', ');
};
